// Pure rolling-window FPS math shared by both the live HUD and any
// future benchmark surface. Timestamps are millisecond-scale
// (performance.now); the caller pushes on each event (rVFC firing,
// decode completion) and asks for the smoothed rate over a rolling
// window (default 1 s).
//
// pruneWindow() mutates the array in place so the caller can keep
// pushing without accumulating unbounded memory — a 1 s window at
// 60 fps holds ~60 numbers, not 3600 after a minute.

const DEFAULT_WINDOW_MS = 1000;

/**
 * Rate over the last `windowMs` ending at `nowMs`, expressed as
 * events per second. Returns null when there are no timestamps.
 * Assumes `timestamps` is sorted ascending (as it is when the caller
 * only ever `push`es fresh `performance.now()` values).
 */
export function computeFps(
  timestamps: readonly number[],
  nowMs: number,
  windowMs: number = DEFAULT_WINDOW_MS
): number | null {
  if (timestamps.length === 0) return null;
  const cutoff = nowMs - windowMs;
  let count = 0;
  for (let i = timestamps.length - 1; i >= 0; i--) {
    if (timestamps[i] < cutoff) break;
    count += 1;
  }
  return (count * 1000) / windowMs;
}

/** Drop timestamps older than `nowMs - windowMs`. Mutates in place. */
export function pruneWindow(
  timestamps: number[],
  nowMs: number,
  windowMs: number = DEFAULT_WINDOW_MS
): void {
  const cutoff = nowMs - windowMs;
  while (timestamps.length > 0 && timestamps[0] < cutoff) {
    timestamps.shift();
  }
}
